import z from "zod";

/**
 * Providers a customer key may be registered for. The backend rejects any other,
 * and a provider it returns that is missing here fails the whole list parse.
 *
 * OpenRouter is not a vendor of its own — it resells the other two plus Google,
 * so its key serves any model. A direct key wins for its own provider.
 */
export const AIProviderIdSchema = z.enum(["anthropic", "openai", "openrouter"]);

/**
 * An organization may hold at most one key per provider, so this bounds how many
 * keys it can have at all.
 */
export const AI_PROVIDER_IDS = AIProviderIdSchema.options;

/**
 * A turn the provider refuses flips the row to invalid server-side, so a key can
 * go bad without an owner touching it.
 */
export const AIProviderKeyStatusSchema = z.enum(["active", "invalid"]);

/**
 * The key itself never leaves the server: keyHint is the only part of it that
 * arrives here. lastError is `omitempty` on the Go side, so an unset one arrives
 * absent rather than null.
 */
export const AIProviderKeySchema = z.object({
  id: z.string(),
  provider: AIProviderIdSchema,
  label: z.string(),
  keyHint: z.string(),
  status: AIProviderKeyStatusSchema,
  lastError: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined),
  enabled: z.boolean(),
});

export const AIProviderKeysResponseSchema = z.object({
  credentials: z
    .array(AIProviderKeySchema)
    .nullish()
    .transform((v) => v ?? []),
});

export const CredentialIdParamsSchema = z.object({
  credentialId: z.string(),
});

export const CreateAIProviderKeyInputSchema = z.object({
  provider: AIProviderIdSchema,
  label: z.string().trim().min(1).max(128),
  apiKey: z.string().min(1),
});

export type AIProviderId = z.infer<typeof AIProviderIdSchema>;
export type AIProviderKeyStatus = z.infer<typeof AIProviderKeyStatusSchema>;
export type AIProviderKey = z.infer<typeof AIProviderKeySchema>;
